import React from 'react';
import { History, Trash2, Upload, Zap } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных расчетов электроэнергии
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <History className="w-5 h-5 mr-2 text-blue-500" />
          История расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты стоимости электроэнергии.
        </p>
      </div>
    );
  }

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <History className="w-5 h-5 mr-2 text-blue-500" />
          История расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'запись' : savedCalculations.length < 5 ? 'записи' : 'записей'}
        </span>
      </div>

      <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1">
        {savedCalculations
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .map((calc) => (
            <div 
              key={calc.id} 
              className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex justify-between items-start mb-1">
                <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                  {calc.name}
                </h4>
                <div className="flex items-center space-x-1">
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<Upload className="w-3.5 h-3.5" />}
                    onClick={() => loadSavedCalculation(calc.id)}
                    title="Загрузить параметры"
                  />
                  <Button
                    size="xs"
                    variant="ghost"
                    icon={<Trash2 className="w-3.5 h-3.5 text-red-500" />}
                    onClick={() => deleteSavedCalculation(calc.id)}
                    title="Удалить"
                  />
                </div>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                {formatDate(calc.date)}
              </div>
              <div className="grid grid-cols-2 gap-x-1 gap-y-1 text-sm">
                <div className="flex items-center text-gray-700 dark:text-gray-300">
                  <Zap className="w-3.5 h-3.5 text-yellow-500 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    {formatNumber(calc.results.totalConsumption, 0)} кВт⋅ч
                  </span>
                </div>
                <div className="text-right font-medium text-green-600 dark:text-green-400">
                  {formatCurrency(calc.results.finalCost, 0)}
                </div>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default SavedCalculationsSidebar;